import React from "react";
import Button from "../../../../../Components/Common/Button/Button";
import SearchableSelect from "../../../../../Components/Common/SearchableSelect/SearchableSelect";
import ChipEmailInput from "../../../../../Components/Common/ChipEmailInput/ChipEmailInput";
import { UserOption } from "../../../../../hooks/useUsersByRole";
import { useLenderOfficers } from "../../../../../hooks/useLenderOfficers";
import useGetBanks, { Bank } from "../../../../../hooks/useGetBanks";
import useGetBranches, { Branch } from "../../../../../hooks/useGetBranches";
import { NewProjectFormValues } from "../types";
import { FormikErrors, FormikTouched } from "formik";

interface Step4LenderProps {
  values: NewProjectFormValues;
  errors: FormikErrors<NewProjectFormValues>;
  touched: FormikTouched<NewProjectFormValues>;
  setFieldValue: (name: string, value: string | string[]) => void;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onNext: () => void;
  onBack: () => void;
  /** Admin only: send the user to Manage Users to create a missing lending officer. */
  onCreateUser?: () => void;
  /** Admin only: send the user to Manage Banks to add a missing bank / branch. */
  onCreateBank?: () => void;
  onCreateBranch?: () => void;
}

export default function Step4Lender({
  values,
  errors,
  touched,
  setFieldValue,
  onChange,
  onNext,
  onBack,
  onCreateUser,
  onCreateBank,
  onCreateBranch,
}: Step4LenderProps) {
  const bank = useGetBanks();
  const branch = useGetBranches(values.lenderBankId);
  const lender = useLenderOfficers(values.lenderBranchId);

  return (
    <div className="stepPanel active">
      <div className="card">
        <div className="cardTitle">Lender Information</div>
        <div className="fieldGroup cols2">
          <div className="field">
            <label>
              Bank <span className="req">*</span>
            </label>
            <SearchableSelect<Bank>
              id="lenderBankId"
              value={values.lenderBankId}
              valueLabel={
                values.lenderBankId && values.bankName ? values.bankName : ""
              }
              options={bank.options}
              onCreateNew={onCreateBank}
              createNewLabel="+ Add new bank"
              getOptionValue={(b) => b.id}
              getOptionLabel={(b) => b.name}
              placeholder={bank.loading ? "Loading..." : "Search bank..."}
              error={Boolean(touched.lenderBankId && errors.lenderBankId)}
              apiSearch
              onSearch={bank.onSearch}
              hasMore={bank.hasMore && !bank.loading}
              onLoadMore={bank.onLoadMore}
              onChange={(id, selected) => {
                setFieldValue("lenderBankId", id);
                setFieldValue("bankName", selected?.name ?? "");
                setFieldValue("lenderBranchId", "");
                setFieldValue("bankLocation", "");
                setFieldValue("lendingOfficerId", "");
                setFieldValue("lenderName", "");
                setFieldValue("lenderPhone", "");
                setFieldValue("lenderEmail", "");
                setFieldValue("lenderAddr", "");
              }}
            />
            {touched.lenderBankId && errors.lenderBankId && (
              <span className="field-error">{errors.lenderBankId}</span>
            )}
          </div>
          <div className="field">
            <label>
              Branch <span className="req">*</span>
            </label>
            <SearchableSelect<Branch>
              id="lenderBranchId"
              value={values.lenderBranchId}
              valueLabel={
                values.lenderBranchId && values.bankLocation
                  ? values.bankLocation
                  : ""
              }
              options={branch.options}
              onCreateNew={onCreateBranch}
              createNewLabel="+ Add new branch"
              getOptionValue={(b) => b.id}
              getOptionLabel={(b) =>
                b.name + (b.address ? ` - ${b.address}` : "")
              }
              placeholder={branch.loading ? "Loading..." : "Search branch..."}
              disabled={!values.lenderBankId || branch.loading}
              error={Boolean(touched.lenderBranchId && errors.lenderBranchId)}
              apiSearch
              onSearch={branch.onSearch}
              hasMore={branch.hasMore && !branch.loading}
              onLoadMore={branch.onLoadMore}
              onChange={(id, selected) => {
                setFieldValue("lenderBranchId", id);
                setFieldValue("bankLocation", selected?.name ?? "");
                setFieldValue("lendingOfficerId", "");
                setFieldValue("lenderName", "");
                setFieldValue("lenderPhone", "");
                setFieldValue("lenderEmail", "");
                setFieldValue("lenderAddr", "");
              }}
            />
            {touched.lenderBranchId && errors.lenderBranchId && (
              <span className="field-error">{errors.lenderBranchId}</span>
            )}
          </div>
        </div>

        <div className="fieldGroup cols1">
          <div className="field">
            <label>
              Lending Officer <span className="req">*</span>
            </label>
            <SearchableSelect<UserOption>
              id="lendingOfficerId"
              value={values.lendingOfficerId}
              valueLabel={
                values.lendingOfficerId && values.lenderName
                  ? `${values.lenderName} (${values.lenderEmail ?? ""})`
                  : ""
              }
              options={lender.options}
              onCreateNew={onCreateUser}
              createNewLabel="+ Add new lender"
              getOptionValue={(u) => u.id}
              getOptionLabel={(u) =>
                `${u.firstName} ${u.lastName} (${u.email})`
              }
              placeholder={
                lender.loading ? "Loading..." : "Select lending officer..."
              }
              disabled={!values.lenderBranchId}
              error={Boolean(
                touched.lendingOfficerId && errors.lendingOfficerId,
              )}
              apiSearch
              onOpen={lender.triggerFetch}
              onSearch={lender.onSearch}
              hasMore={lender.hasMore && !lender.loading}
              onLoadMore={lender.onLoadMore}
              onChange={(id, user) => {
                setFieldValue("lendingOfficerId", id);
                setFieldValue(
                  "lenderName",
                  user ? `${user.firstName} ${user.lastName}` : "",
                );
                setFieldValue("lenderPhone", user?.phone ?? "");
                setFieldValue("lenderEmail", user?.email ?? "");
                setFieldValue("lenderAddr", user?.address ?? "");
              }}
            />
            {touched.lendingOfficerId && errors.lendingOfficerId && (
              <span className="field-error">{errors.lendingOfficerId}</span>
            )}
          </div>
        </div>

        <div className="fieldGroup cols1">
          <div className="field overflow-auto">
            <label>Others to CC on Reports &amp; Emails</label>
            <ChipEmailInput
              value={values.ccList}
              onChange={(val) => setFieldValue("ccList", val)}
            />
          </div>
        </div>
      </div>

      <div className="btnRow">
        <Button className="btn btnSecondary" onClick={onBack}>
          ← Back
        </Button>
        <Button className="btn btnPrimary" onClick={onNext}>
          Next: Requirements →
        </Button>
      </div>
    </div>
  );
}
